import { Injectable, Signal, signal } from '@angular/core';

export type ToastKind = 'info' | 'success' | 'error';

export interface ToastMessage {
  id: string;
  message: string;
  kind: ToastKind;
}

const createId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private readonly internalToasts = signal<ToastMessage[]>([]);
  readonly toasts: Signal<ToastMessage[]> = this.internalToasts.asReadonly();

  show(message: string, kind: ToastKind = 'info', durationMs = 5000): void {
    const toast: ToastMessage = { id: createId(), message, kind };
    this.internalToasts.update((current) => [...current, toast]);
    if (durationMs > 0) {
      window.setTimeout(() => this.dismiss(toast.id), durationMs);
    }
  }

  success(message: string, durationMs?: number): void {
    this.show(message, 'success', durationMs);
  }

  error(message: string, durationMs?: number): void {
    this.show(message, 'error', durationMs);
  }

  info(message: string, durationMs?: number): void {
    this.show(message, 'info', durationMs);
  }

  dismiss(id: string): void {
    this.internalToasts.update((current) => current.filter((toast) => toast.id !== id));
  }
}
